import './CertificateCard.scss';
import Subtitle from 'components/TextComps/Subtitle';
import Paragraph from 'components/TextComps/Paragraph';
import { CertificationTest, CertificationTestStatus, Prerequisite } from 'types/module';
import defaultCertificateBadge from 'images/Certification/DefaultCertificateBadge.svg';
import { ReactComponent as PadlockFilled } from 'images/icons/PadlockFilled.svg';
import { ReactComponent as Membership } from 'images/icons/MembershipFilled.svg';
import { ReactComponent as Document } from 'images/icons/DocumentThin.svg';
import { ReactComponent as Download } from 'images/icons/Download.svg';
import { Tooltip } from '@trackman/web-shared-components';
import { PrerequisitesTooltip } from './PrerequisitesTooltip/PrerequisitesTooltip';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import { useServices, useStores } from 'index';
import { useMemo, useState } from 'react';

type CertificateCardProps = {
  id: string;
  title: string;
  status: CertificationTestStatus;
  progress: number;
  description: string;
  prerequisites: Prerequisite[];
  badgeImageUrl: string;
};
export const CertificateCard = ({ id, title, status, progress, description, prerequisites, badgeImageUrl }: CertificateCardProps) => {
  const { t } = useTranslation();
  const service = useServices().certificatesService;
  const notificationsStore = useStores().notificationStore;

  const certificateLinkClasses = useMemo(
    () => (status === 'Locked' || status === 'ComingSoon' ? 'certification-link link-disabled' : 'certification-link'),
    [status]
  );

  const [isDownloading, setIsDownloading] = useState(false);

  const downloadPdf = async () => {
    setIsDownloading(true);
    notificationsStore.addToast(
      t('PreparingDownload', { ns: 'certificate' }),
      t('YourCertificateDownloadIsBeingPrepared', { ns: 'certificate' }),
      'info'
    );

    try {
      await service.download(id, title);
      setIsDownloading(false);
      notificationsStore.addToast(
        t('DownloadReady', { ns: 'certificate' }),
        t('YourCertificateDownloadIsReady', { ns: 'certificate' }),
        'success'
      );
    } catch {
      setIsDownloading(false);
      notificationsStore.addToast(
        t('DownloadFailed', { ns: 'certificate' }),
        t('ThereWasAnErrorPreparingYourDownload', { ns: 'certificate' }),
        'error'
      );
    }
  };

  return (
    <div className={status === 'Completed' ? 'certificate-card certificate-card__completed' : 'certificate-card'}>
      <a className={certificateLinkClasses}></a>
      <div className='header'>
        <img src={badgeImageUrl ? badgeImageUrl : defaultCertificateBadge} alt={title ?? ''} />
        {(status === 'ComingSoon' || status === 'Completed' || progress > 0) && <CertificationBadge status={status} progress={progress} />}
      </div>

      <CertificationTitle title={title} status={status} prerequisites={prerequisites} />

      {status == 'Completed' ? (
        <Button className='download-button' startIcon={<Download />} onClick={downloadPdf} disabled={isDownloading}>
          {t('DownloadPDF', { ns: 'learningHub' })}
        </Button>
      ) : (
        <Paragraph>
          <>{description}</>
        </Paragraph>
      )}
    </div>
  );
};
type CertificationTitleProps = Pick<CertificationTest, 'title' | 'status' | 'prerequisites'>;

const CertificationTitle = ({ title, status, prerequisites }: CertificationTitleProps) => {
  const { t } = useTranslation();
  const hasIcon = useMemo(() => status === 'Locked' || status === 'Completed', [status]);

  return (
    <>
      {hasIcon ? (
        <div className='title-with-icon-wrapper'>
          <TitleIconBox status={status} prerequisites={prerequisites} />
          {status === 'Completed' ? (
            <div className='completed-title'>
              <Subtitle>
                <>{title}</>
              </Subtitle>
              <Paragraph>
                <>{t('DownloadYourCertificate', { ns: 'learningHub' })}</>
              </Paragraph>
            </div>
          ) : (
            <Subtitle className='certificate-card-title'>
              <>{title}</>
            </Subtitle>
          )}
        </div>
      ) : (
        <Subtitle>
          <>{title}</>
        </Subtitle>
      )}
    </>
  );
};

const TitleIconBox = ({ status, prerequisites }: Pick<CertificationTitleProps, 'status' | 'prerequisites'>) => {
  const hasPrerequisites = useMemo(() => prerequisites?.length > 0, [prerequisites]);

  return (
    <>
      {status === 'Completed' ? (
        <div className='file-type'>
          <Document />
          <div>PDF</div>
        </div>
      ) : (
        <div className='blocked-certificate-icon'>
          {hasPrerequisites ? (
            <Tooltip
              title={
                <div style={{ textAlign: 'center' }}>
                  <PrerequisitesTooltip prerequisitesTitles={prerequisites.map((p) => p.title)}></PrerequisitesTooltip>
                </div>
              }
              className='certificate-card-tooltip'
            >
              <PadlockFilled></PadlockFilled>
            </Tooltip>
          ) : (
            <PadlockFilled></PadlockFilled>
          )}
        </div>
      )}
    </>
  );
};

type CertificationBadgeProps = {
  status: CertificationTestStatus;
  progress: number;
};

const CertificationBadge = ({ status, progress }: CertificationBadgeProps) => {
  const { t } = useTranslation();

  return (
    <div
      className={
        status === 'Completed'
          ? 'certificate-badge certificate-badge__completed'
          : status === 'ComingSoon'
            ? 'certificate-badge certificate-badge__coming-soon'
            : progress > 0
              ? 'certificate-badge certificate-badge__in-progress'
              : ''
      }
    >
      {status === 'Completed' ? (
        <>
          <Membership /> <span>{t('Completed', { ns: 'learningHub' })}</span>
        </>
      ) : status === 'ComingSoon' ? (
        <span>{t('ComingSoon', { ns: 'learningHub' })}</span>
      ) : progress > 0 ? (
        <span>{t('InProgress', { progress, ns: 'learningHub' })}</span>
      ) : (
        <></>
      )}
    </div>
  );
};
